import {
  logbookChartDayLabel as dayLabel,
  logbookChartGapLeadingZeros as gapLeadingZeros,
  logbookChartNormalize as normalize,
  logbookChartTickIndices as tickIndices,
} from "@healthos/shared";
import { ChartLineUp } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef } from "react";
import uPlot from "uplot";
import { EmptyState } from "../empty-state";

/** Logbook's own 7/14/28-day toggle — Snapshot's `TrendChart` uses a
 * different range set, so this isn't a reuse of its constant. */
export const LOGBOOK_DETAIL_RANGES = [7, 14, 28] as const;
export type LogbookDetailRange = (typeof LOGBOOK_DETAIL_RANGES)[number];

/** One bucketed day. Mirrors core-api's `DailyPoint` structurally rather
 * than importing it — this package renders whatever shape it's given. */
export interface LogbookTrendPoint {
  day: string;
  count: number;
}

/** One day's chart value. `null` means no qualifying reading that day —
 * distinct from a real `0`. Mirrors core-api's `LogbookMetricPoint`. */
export interface LogbookMetricPoint {
  day: string;
  value: number | null;
}

/** One selectable chart series. Mirrors core-api's `LogbookMetricOption`. */
export interface LogbookMetricOption {
  key: string;
  label: string;
  unit: string | null;
  series: LogbookMetricPoint[];
}

/** Mirrors `buildLogbookTrend`'s return shape. `series` only backs the
 * empty-state gate and footer caption now; `metrics` is what's plotted. */
export interface LogbookTrendData {
  series: LogbookTrendPoint[];
  sufficientData: boolean;
  metrics: LogbookMetricOption[];
}

/** Pseudo-key for the "every metric at once" overlay — never a real
 * `LogbookMetricOption.key`, so it can't collide with one. */
export const ALL_METRICS_KEY = "__all__";

/** Per-metric colour order for the "All" overlay, cycled by index. CSS
 * custom-property names, not literal colours — canvas can't resolve `var()`
 * directly, so `resolveMetricColors` below does that for the plotted lines
 * while the legend (plain DOM) reads these names directly. */
const METRIC_COLORS = [
  "--color-teal-700",
  "--color-purple-700",
  "--color-amber-700",
  "--color-rose-700",
  "--color-blue-700",
  "--color-leaf",
  "--color-gray-700",
];

/** The day-range toggle. Hook-free so it's directly testable — this package
 * has no jsdom/Testing Library. */
export function LogbookRangeToggle({
  days,
  onDays,
}: {
  days: LogbookDetailRange;
  onDays: (next: LogbookDetailRange) => void;
}) {
  return (
    <fieldset className="logbook-chart-range">
      <legend className="sr-only">Chart range</legend>
      {LOGBOOK_DETAIL_RANGES.map((r) => (
        <button key={r} type="button" aria-pressed={days === r} onClick={() => onDays(r)}>
          {r}d
        </button>
      ))}
    </fieldset>
  );
}

/** The metric selector, plus a trailing "All" that overlays every metric.
 * Renders nothing with only one metric — a one-option tab row is chrome. */
export function LogbookMetricTabs({
  metrics,
  selected,
  onSelect,
}: {
  metrics: LogbookMetricOption[];
  selected: string;
  onSelect: (key: string) => void;
}) {
  if (metrics.length < 2) return null;
  return (
    <fieldset className="logbook-metric-tabs">
      <legend className="sr-only">Chart metric</legend>
      {metrics.map((m) => (
        <button
          key={m.key}
          type="button"
          aria-pressed={selected === m.key}
          onClick={() => onSelect(m.key)}
        >
          {m.label}
        </button>
      ))}
      <button
        type="button"
        aria-pressed={selected === ALL_METRICS_KEY}
        onClick={() => onSelect(ALL_METRICS_KEY)}
      >
        All
      </button>
    </fieldset>
  );
}

/** Reads the ink tokens this chart needs off the host element, so colour
 * stays in CSS rather than hardcoded here. */
function chartInk(host: HTMLElement) {
  const cs = getComputedStyle(host);
  const v = (name: string) => (cs.getPropertyValue(name) || "").trim() || "transparent";
  return {
    bar: v("--logbook-bar"),
    line: v("--logbook-line"),
    point: v("--color-paper"),
    stripe: v("--logbook-stripe"),
  };
}

/** Resolves `METRIC_COLORS`' custom-property names to literal computed
 * values for canvas use — a 2D context can't resolve `var()` directly. */
function resolveMetricColors(host: HTMLElement): string[] {
  const cs = getComputedStyle(host);
  return METRIC_COLORS.map((name) => (cs.getPropertyValue(name) || "").trim() || "transparent");
}

/** Room to the LEFT of the plot for the y-axis labels, in CSS pixels. */
const Y_GUTTER = 40;
/** The strip under the plot the x-axis date labels live in. */
const LANE_H = 16;
/** Upper bound on simultaneous x-axis date labels; actual count is
 * `min(MAX_X_TICKS, series.length)`. */
const MAX_X_TICKS = 6;

/** Canvas normalises any colour it accepts to hex, which is parseable. */
function withAlpha(ctx: CanvasRenderingContext2D, colour: string, alpha: number): string {
  const before = ctx.fillStyle;
  ctx.fillStyle = colour;
  const hex = String(ctx.fillStyle);
  ctx.fillStyle = before;
  if (!hex.startsWith("#") || hex.length < 7) return hex;
  const to = (i: number) => Number.parseInt(hex.slice(i, i + 2), 16);
  return `rgba(${to(1)}, ${to(3)}, ${to(5)}, ${alpha})`;
}

/** How long one bar/line's own entrance animation takes, in ms. */
const REVEAL_DURATION_MS = 450;
/** Extra delay before each successive bar/line starts its own reveal, so a
 * multi-bar or multi-line chart sweeps in rather than every column/line
 * finishing at the same instant. */
const REVEAL_STAGGER_MS = 45;

/** Less front-loaded than ease-out-cubic, so the sweep stays perceptible
 * longer instead of mostly finishing in the first third. */
function easeOutQuad(t: number): number {
  return 1 - (1 - t) ** 2;
}

/** One bar/line's eased progress, offset by its stagger slot and clamped to
 * [0, 1] so an index whose turn hasn't come reads as 0, not negative. */
function staggeredProgress(elapsedMs: number, index: number): number {
  const t = Math.max(0, Math.min(1, (elapsedMs - index * REVEAL_STAGGER_MS) / REVEAL_DURATION_MS));
  return easeOutQuad(t);
}

/** Total time every staggered item needs to fully finish — the last
 * item's own stagger delay, plus its own full reveal duration. */
function totalRevealMs(itemCount: number): number {
  return REVEAL_DURATION_MS + Math.max(0, itemCount - 1) * REVEAL_STAGGER_MS;
}

/** The line's stroke during its staggered entrance: real colour up to the
 * current progress, transparent beyond it (sharp cutoff), so it sweeps in
 * left-to-right. Plain `colour` once the animation finishes. */
function lineStroke(
  u: uPlot,
  colour: string,
  elapsedMs: number,
  staggerIndex: number,
): string | CanvasGradient {
  const progress = staggeredProgress(elapsedMs, staggerIndex);
  if (progress >= 1) return colour;

  const left = u.bbox.left;
  const span = u.bbox.width || 1;
  const clear = withAlpha(u.ctx, colour, 0);
  const cutoff = Math.max(0, Math.min(1, progress));

  const stops: Array<[number, string]> = [
    [0, colour],
    [Math.max(0, cutoff - 0.001), colour],
    [cutoff, clear],
    [1, clear],
  ];

  const gradient = u.ctx.createLinearGradient(left, 0, left + span, 0);
  let prev = -1;
  for (const [offset, tone] of stops) {
    const safe = Math.max(Math.min(offset, 1), prev + 1e-6);
    if (safe > 1) continue;
    gradient.addColorStop(safe, tone);
    prev = safe;
  }
  return gradient;
}

/** Each bar's top edge rises from the baseline over `elapsedMs`, staggered
 * by day-index so columns sweep in left-to-right. Lines need no equivalent
 * — their reveal lives entirely in `lineStroke`'s gradient. */
function growFromBottom(
  values: ReadonlyArray<number | null>,
  elapsedMs: number,
): Array<number | null> {
  return values.map((v, i) => (v === null ? null : v * staggeredProgress(elapsedMs, i)));
}

/** One animation frame of `uData`'s bar series — "All" mode has no bars and
 * its lines never change data, only their stroke gradients. */
function revealFrame(
  uData: uPlot.AlignedData,
  isAll: boolean,
  elapsedMs: number,
): uPlot.AlignedData {
  if (isAll) return uData;
  const xs = uData[0];
  const barValues = growFromBottom(uData[1] as ReadonlyArray<number | null>, elapsedMs);
  return [xs, barValues, uData[2]] as unknown as uPlot.AlignedData;
}

export function LogbookTrendChart({
  data,
  days,
  onDays,
  caption,
  selectedMetricKey,
  onSelectMetric,
}: {
  data: LogbookTrendData;
  days: LogbookDetailRange;
  onDays: (next: LogbookDetailRange) => void;
  /** Tab-specific copy; this component renders it, doesn't compute it. */
  caption: string;
  /** Controlled, not internal state: for Food/Stress/Health this is the
   * same value driving the history list's type filter one level up, so one
   * tab row changes both. `ALL_METRICS_KEY` selects the "All" overlay. */
  selectedMetricKey: string;
  onSelectMetric: (key: string) => void;
}) {
  const { metrics } = data;
  const hostRef = useRef<HTMLDivElement>(null);
  const yLabs = useRef<Array<HTMLSpanElement | null>>([null, null]);
  const xLabs = useRef<Array<HTMLSpanElement | null>>(Array(MAX_X_TICKS).fill(null));
  const tooltipRef = useRef<HTMLDivElement>(null);

  const selectedKey = selectedMetricKey;
  const isAll = metrics.length > 1 && selectedKey === ALL_METRICS_KEY;
  const selectedMetric = metrics.find((m) => m.key === selectedKey) ?? metrics[0];

  // Every metric's series is the full fetched window; sliced to the active range here.
  const days7 = useMemo(() => data.series.slice(-days), [data.series, days]);
  const reported = useMemo(() => days7.filter((p) => p.count > 0).length, [days7]);

  const windowLength = days7.length;
  const dayKeys = useMemo(() => days7.map((p) => p.day), [days7]);

  const activeMetrics = useMemo(() => {
    if (!isAll) return selectedMetric ? [selectedMetric] : [];
    // A metric with nothing real logged normalizes to a meaningless flat 50
    // line — left out of the "All" overlay entirely.
    return metrics.filter((m) =>
      dayKeys.some((day) => {
        const value = m.series.find((p) => p.day === day)?.value ?? null;
        return value !== null && value !== 0;
      }),
    );
  }, [isAll, metrics, selectedMetric, dayKeys]);

  const uData = useMemo(() => {
    const xs = Array.from({ length: windowLength }, (_, i) => i);
    if (isAll) {
      const lines = activeMetrics.map((m) => {
        const values = dayKeys.map((day) => m.series.find((p) => p.day === day)?.value ?? null);
        return gapLeadingZeros(normalize(values));
      });
      return [xs, ...lines] as unknown as uPlot.AlignedData;
    }
    const metric = activeMetrics[0];
    const values = metric
      ? dayKeys.map((day) => metric.series.find((p) => p.day === day)?.value ?? null)
      : dayKeys.map(() => null);
    // Bars keep raw values; the line nulls its leading stretch before the
    // first real reading, so it never drags a flat tail along the axis.
    return [xs, values, gapLeadingZeros(values)] as unknown as uPlot.AlignedData;
  }, [windowLength, dayKeys, isAll, activeMetrics]);

  // Nothing reported means EmptyState renders instead; this effect skips.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !data.sufficientData) return;
    const ink = chartInk(host);
    const resolvedMetricColors = resolveMetricColors(host);
    const bars = uPlot.paths.bars?.({ size: [0.55, 22], align: 0 });
    const line = uPlot.paths.spline?.(); // spline, not linear, for a smoother curve
    const ticks = tickIndices(windowLength, MAX_X_TICKS);

    const allValues = isAll ? uData.slice(1).flat() : (uData[1] as Array<number | null>);
    const numericValues = (allValues as Array<number | null>).filter(
      (v): v is number => v !== null,
    );
    const max = isAll ? 100 : Math.max(...numericValues, 1);
    const min = isAll ? 0 : 0;

    // Mutated by the reveal-animation loop, read fresh by stroke callbacks
    // on every redraw — a ref since `series` is built once per effect run.
    const animState = { elapsedMs: 0 };

    const series: uPlot.Series[] = isAll
      ? [
          {},
          ...activeMetrics.map((m, i) => {
            const color = resolvedMetricColors[i % resolvedMetricColors.length] ?? ink.line;
            return {
              stroke: (u: uPlot) => lineStroke(u, color, animState.elapsedMs, i),
              width: 2,
              fill: "transparent",
              points: { show: true, size: 4, fill: color, stroke: ink.point, width: 1 },
              paths: line,
              label: m.label,
            } satisfies uPlot.Series;
          }),
        ]
      : [
          {},
          {
            stroke: "transparent",
            width: 0,
            fill: ink.bar,
            points: { show: false },
            paths: bars,
          },
          // The movement line draws through the same points the bars represent.
          {
            stroke: (u: uPlot) => lineStroke(u, ink.line, animState.elapsedMs, 0),
            width: 2,
            fill: "transparent",
            points: { show: true, size: 5, fill: ink.line, stroke: ink.point, width: 1.5 },
            paths: line,
          },
        ];

    const opts: uPlot.Options = {
      width: host.clientWidth || 300,
      height: host.clientHeight || 180,
      legend: { show: false },
      cursor: { y: false, drag: { x: false, y: false, setScale: false } },
      axes: [{ show: false }, { show: false }],
      // Left gutter for y labels, bottom lane for x labels — drawn as
      // absolutely-positioned spans below, not uPlot's own axis renderer.
      padding: [8, 8, LANE_H, Y_GUTTER],
      // Half a unit of x-scale padding so the first/last bars aren't clipped at the edge.
      scales: {
        x: { time: false, range: () => [-0.5, windowLength - 0.5] },
        y: { range: () => [min, max] },
      },
      series,
      hooks: {
        // Weekend columns get a faint stripe.
        drawClear: [
          (u) => {
            u.ctx.save();
            u.ctx.fillStyle = ink.stripe;
            for (let i = 0; i < windowLength; i++) {
              const day = dayKeys[i];
              if (!day) continue;
              const dow = new Date(`${day}T00:00:00Z`).getUTCDay();
              if (dow !== 0 && dow !== 6) continue;
              const x0 = Math.max(u.valToPos(i - 0.5, "x", true), u.bbox.left);
              const x1 = Math.min(u.valToPos(i + 0.5, "x", true), u.bbox.left + u.bbox.width);
              if (x1 > x0) u.ctx.fillRect(x0, u.bbox.top, x1 - x0, u.bbox.height);
            }
            u.ctx.restore();
          },
        ],
        draw: [
          (u) => {
            const dpr = devicePixelRatio;
            const left = u.bbox.left / dpr;
            const top = u.bbox.top / dpr;
            const bottom = (u.bbox.top + u.bbox.height) / dpr;
            const gutter = left - 8;

            const placeY = (i: number, text: string, y: number) => {
              const el = yLabs.current[i];
              if (!el) return;
              el.textContent = text;
              el.style.transform = `translate(${gutter}px, ${y}px) translate(-100%, -50%)`;
            };
            // "All" normalizes to a shared 0-100 scale, not a real unit, so
            // it's labelled "High"/"Low" instead of a number. No unit
            // suffix otherwise — the gutter is sized for a short number.
            placeY(0, isAll ? "High" : `${max}`, top);
            placeY(1, isAll ? "Low" : "0", bottom);

            for (let i = 0; i < MAX_X_TICKS; i++) {
              const el = xLabs.current[i];
              if (!el) continue;
              const seriesIdx = ticks[i];
              const day = seriesIdx === undefined ? undefined : dayKeys[seriesIdx];
              if (seriesIdx === undefined || !day) {
                el.textContent = "";
                continue;
              }
              const isLast = seriesIdx === windowLength - 1;
              const isFirst = seriesIdx === 0;
              const x = u.valToPos(seriesIdx, "x", true) / dpr;
              const anchor = isLast ? "translateX(-100%)" : isFirst ? "" : "translateX(-50%)";
              el.textContent = isLast ? "Today" : dayLabel(day);
              el.style.transform = `translate(${x}px, ${bottom + LANE_H + 4}px) ${anchor}`;
            }
          },
        ],
        // Tooltip reads the metric's raw value/unit by day, not the
        // normalized (0-100) plotted series, which is for comparison only.
        setCursor: [
          (u) => {
            const tip = tooltipRef.current;
            if (!tip) return;
            const idx = u.cursor.idx;
            const day = idx === null || idx === undefined ? undefined : dayKeys[idx];
            if (!day) {
              tip.style.opacity = "0";
              return;
            }
            const rows = activeMetrics.map((m) => {
              const value = m.series.find((p) => p.day === day)?.value ?? null;
              const unit = m.unit ? ` ${m.unit}` : "";
              const text = value === null ? "No data" : `${value}${unit}`;
              return isAll ? `${m.label}: ${text}` : text;
            });
            tip.innerHTML = "";
            const dateLine = document.createElement("div");
            dateLine.className = "logbook-tooltip-date";
            dateLine.textContent = idx === windowLength - 1 ? "Today" : dayLabel(day);
            tip.appendChild(dateLine);
            for (const row of rows) {
              const rowEl = document.createElement("div");
              rowEl.textContent = row;
              tip.appendChild(rowEl);
            }
            const x = (u.cursor.left ?? 0) + 10;
            const y = (u.cursor.top ?? 0) - 10;
            tip.style.transform = `translate(${x}px, ${y}px) translateY(-100%)`;
            tip.style.opacity = "1";
          },
        ],
      },
    };

    // Reduced-motion: show real data straight away, never a half-grown bar.
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const itemCount = isAll ? activeMetrics.length : windowLength;
    const totalMs = totalRevealMs(itemCount);
    animState.elapsedMs = reduceMotion ? totalMs : 0;

    const plot = new uPlot(opts, reduceMotion ? uData : revealFrame(uData, isAll, 0), host);

    let rafId: number | null = null;
    if (!reduceMotion) {
      const start = performance.now();
      const tick = (now: number) => {
        const elapsed = now - start;
        animState.elapsedMs = elapsed;
        // No second argument: setData only redraws when resetScales !== false
        // — passing false silently updates data with no repaint.
        plot.setData(revealFrame(uData, isAll, elapsed));
        rafId = elapsed < totalMs ? requestAnimationFrame(tick) : null;
      };
      rafId = requestAnimationFrame(tick);
    }

    const observer = new ResizeObserver(() => {
      if (host.clientWidth > 0) {
        plot.setSize({ width: host.clientWidth, height: host.clientHeight });
      }
    });
    observer.observe(host);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      observer.disconnect();
      plot.destroy();
    };
  }, [uData, data.sufficientData, windowLength, dayKeys, isAll, activeMetrics]);

  if (!data.sufficientData) {
    // The toggle renders here too: a member with too few entries in the
    // current range but enough in a wider one needs it to reach that range.
    return (
      <div className="logbook-chart">
        <LogbookMetricTabs metrics={metrics} selected={selectedKey} onSelect={onSelectMetric} />
        <div className="logbook-chart-head">
          <LogbookRangeToggle days={days} onDays={onDays} />
        </div>
        <EmptyState
          icon={<ChartLineUp size={20} aria-hidden="true" />}
          headline="Not enough entries yet"
          body="Log a few more days here and a trend will take shape."
        />
        {caption ? <p className="logbook-chart-caption">{caption}</p> : null}
      </div>
    );
  }

  return (
    <figure className="logbook-chart">
      <LogbookMetricTabs metrics={metrics} selected={selectedKey} onSelect={onSelectMetric} />
      <div className="logbook-chart-head">
        <LogbookRangeToggle days={days} onDays={onDays} />
      </div>
      <div className="logbook-chart-box" key={`${selectedKey}-${days}`}>
        <div className="logbook-uplot" data-testid="logbook-chart-canvas" ref={hostRef} />
        {[0, 1].map((i) => (
          <span
            key={`y-${i}`}
            className="logbook-yl"
            aria-hidden="true"
            ref={(el) => {
              yLabs.current[i] = el;
            }}
          />
        ))}
        {Array.from({ length: MAX_X_TICKS }, (_, i) => i).map((i) => (
          <span
            key={`x-${i}`}
            className="logbook-xl"
            aria-hidden="true"
            ref={(el) => {
              xLabs.current[i] = el;
            }}
          />
        ))}
        <div className="logbook-tooltip" aria-hidden="true" ref={tooltipRef} />
      </div>
      <figcaption className="snap-chart-foot">
        {isAll ? (
          <ul className="snap-chart-key">
            {activeMetrics.map((m, i) => (
              <li key={m.key}>
                <i
                  className="logbook-key-dot"
                  aria-hidden="true"
                  style={{ background: `var(${METRIC_COLORS[i % METRIC_COLORS.length]})` }}
                />
                {m.label}
              </li>
            ))}
          </ul>
        ) : (
          <ul className="snap-chart-key">
            <li>
              <i className="snap-key-bar" aria-hidden="true" />
              Logged by you
            </li>
          </ul>
        )}
        <span className="snap-chart-count">
          {reported} of the last {windowLength} days logged.
        </span>
      </figcaption>
      {caption ? <p className="logbook-chart-caption">{caption}</p> : null}
    </figure>
  );
}
